import { getVariant, getOptions } from "product-utils";

/**
 * Product Media Component
 *
 * Handles the image carousel for product media using Splide.js
 * Supports dynamic variant image swapping when variant selection changes.
 */
class ProductMedia extends HTMLElement {
  constructor() {
    super();
    this.initializeProperties();
    this.loadVariantImageData();
    this.setupSplideOptions();
  }

  /**
   * Initialize component properties
   */
  initializeProperties() {
    // DOM elements
    this.mediaSplide = this.querySelector("#media-splide");
    this.thumbnailSplide = this.querySelector("#thumbnail-splide");
    this.counter = this.querySelector(".splide__counter");
    this.pagination = this.querySelector(".splide__pagination");
    this.productNode = document.querySelector("[data-template-product]");

    // Component state
    this.thumbsInitialized = false;
    this.mainSplide = null;
    this.thumbs = null;
    this.variantImagesData = [];
    this.optionInputs = null;
    this.boundOptionChangeHandler = null;
    this.mainVideoPlayingIndex = null;
  }

  /**
   * Parse variant image data from JSON in the DOM
   * @returns {Array} The parsed variant images data or empty array if none found
   */
  loadVariantImageData() {
    const jsonDataElement = this.querySelector("script[data-images-attached-to-variants]");
    if (jsonDataElement) {
      try {
        this.variantImagesData = JSON.parse(jsonDataElement.textContent);
        return this.variantImagesData;
      } catch (e) {
        console.error("Error parsing variant images data:", e);
        this.variantImagesData = [];
        return [];
      }
    } else {
      // Log as debug instead of warning since this might be expected in some cases
      console.info("No variant images data found in the DOM");
      this.variantImagesData = [];
      return [];
    }
  }

  /**
   * Setup Splide carousel configuration options
   */
  setupSplideOptions() {
    // Main carousel options
    this.mediaOptions = {
      type: "slide",
      pagination: true,
      arrows: true,
      focus: 0,
      omitEnd: true,
      flickPower: 500,
      speed: 900,
      mediaQuery: "min",
      perPage: 1.05,
      gap: 2,
      autoHeight: true,
      // Ensure HTML5 videos do not autoplay in the main gallery
      video: {
        autoplay: false,
        mute: false,
        // Remove Splide overlay UI and native controls
        disableOverlayUI: true,
        hideControls: true,
        playerOptions: {
          htmlVideo: {
            controls: false,
            playsInline: true,
            disablePictureInPicture: true,
            disableRemotePlayback: true,
            // Don't force muted so user hears audio by default
          },
        },
      },
      breakpoints: {
        990: {
          perPage: 1,
          gap: 0,
        },
      },
    };

    // Thumbnail carousel options
    this.thumbnailOptions = {
      type: "slide",
      gap: 6,
      perPage: 7,
      pagination: false,
      direction: "ttb",
      heightRatio: 7.5,
      cover: true,
      isNavigation: true,
      arrows: false,
    };
  }

  /**
   * Called when the element is added to the DOM
   */
  connectedCallback() {
    this.initializeCarousels();
    this.setupEventListeners();
    this.handleInitialVariant();

    // Initialize pagination directly - ensures dots appear on load
    setTimeout(() => {
      if (this.mainSplide) {
        const { index, length } = this.mainSplide;
        this.updateSlidingPagination(index, length);
      }
    }, 100);
  }

  /**
   * Initialize Splide carousels
   */
  initializeCarousels() {
    // Initialize main carousel
    this.mainSplide = new Splide(this.mediaSplide, this.mediaOptions);

    // Pause any playing video when moving between slides
    this.mainSplide.on("move", (newIndex, oldIndex) => {

      this.classList.add("carousel-active");

      this.pauseAllMainVideos();
      // Clear any inline display:none on wrappers or images across all slides
      try {
        const allSlides = this.mediaSplide?.querySelectorAll?.(".splide__slide");
        if (allSlides && allSlides.length) {
          allSlides.forEach((s) => {
            const wrapper = s.querySelector(".splide__video__wrapper");
            if (wrapper && wrapper.style && wrapper.style.display === "none") wrapper.style.display = "";
            const img = s.querySelector("img.grafikr-product__media__main__image");
            if (img && img.style && img.style.display === "none") img.style.display = "";
          });
        }
      } catch (_) {}
      // Restore only the previously active video slide overlays if applicable
      try {
        const prev = this.mainSplide?.Components?.Slides?.getAt?.(oldIndex)?.slide;
        if (prev && this.isVideoSlide(prev)) {
          const previewImg = prev.querySelector("img.grafikr-product__media__main__image");
          if (previewImg) previewImg.style.display = "";
          const wrapper = prev.querySelector(".splide__video__wrapper");
          if (wrapper) wrapper.style.display = "";
          const overlay = prev.querySelector(".grafikr-product__media__video-play");
          if (overlay) overlay.style.display = "flex";
        }
      } catch (_) {}
    });

    // Wire HTML5 video attributes when a video element is created/loaded
    this.mainSplide.on("video:loaded", () => {
      const currentSlide = this.mainSplide?.Components?.Slides?.getAt?.(this.mainSplide.index)?.slide;
      if (currentSlide) {
        this.wireHtmlVideoAttributesForSlide(currentSlide);
        // Ensure Splide video wrapper is visible
        const wrapper = currentSlide.querySelector(".splide__video__wrapper");
        if (wrapper) {
          try { wrapper.style.display = ""; } catch (_) {}
        }
        // Refresh helps when the injected <video> does not size correctly initially
        this.mainSplide.refresh();
      }
    });

    // Toggle overlay visibility based on playback state
    this.mainSplide.on("video:play", () => {
      const currentSlide = this.mainSplide?.Components?.Slides?.getAt?.(this.mainSplide.index)?.slide;
      if (currentSlide) {
        this.mainVideoPlayingIndex = this.mainSplide.index;
        this.setMainVideoOverlayVisible(currentSlide, false);
        const videoEl = currentSlide.querySelector?.("video");
        const previewImg = currentSlide.querySelector?.("img.grafikr-product__media__main__image");
        const wrapper = currentSlide.querySelector(".splide__video__wrapper");
        if (videoEl) {
          try {
            videoEl.controls = false;
            videoEl.style.display = "";
            videoEl.style.opacity = "1";
          } catch (_) {}
        }
        if (wrapper) {
          try { wrapper.style.display = ""; } catch (_) {}
        }
        if (previewImg) {
          try { previewImg.style.display = "none"; } catch (_) {}
        }
        // Recalculate height when switching to video content
        try { this.mainSplide.refresh(); } catch (_) {}
      }
    });
    this.mainSplide.on("video:pause", () => {
      const currentSlide = this.mainSplide?.Components?.Slides?.getAt?.(this.mainSplide.index)?.slide;
      if (currentSlide) {
        this.mainVideoPlayingIndex = null;
        this.setMainVideoOverlayVisible(currentSlide, true);
        const videoEl = currentSlide.querySelector?.("video");
        const previewImg = currentSlide.querySelector?.("img.grafikr-product__media__main__image");
        const wrapper = currentSlide.querySelector(".splide__video__wrapper");
        // Keep paused frame visible by keeping video displayed and hiding preview
        if (videoEl) {
          try {
            videoEl.style.display = "";
            videoEl.pause(); // ensure paused even if extension misses it
          } catch (_) {}
        }
        if (wrapper) {
          try { wrapper.style.display = ""; } catch (_) {}
        }
        if (previewImg) {
          try { previewImg.style.display = "none"; } catch (_) {}
        }
        // Recalculate height when pausing (e.g., preview restored)
        try { this.mainSplide.refresh(); } catch (_) {}
      }
    });

    // Set up counter and pagination
    if (this.counter) {
      this.mainSplide.on("mounted move", () => {
        const { index, length } = this.mainSplide;
        this.counter.textContent = `${index + 1}/${length}`;

        // Handle pagination sliding window
        this.updateSlidingPagination(index, length);
      });
    }

    // Mount main carousel
    this.mainSplide.mount(window.splide.Extensions);

    // Initialize pagination on mount
    this.mainSplide.on("mounted", () => {
      const { index, length } = this.mainSplide;
      this.updateSlidingPagination(index, length);

      // Set up click listeners for slides after the carousel is mounted
      this.setupMediaClickListeners();
    });

    // Initialize thumbnail carousel on desktop
    if (window.innerWidth >= 990) {
      this.initializeThumbs();
    }
  }

  /**
   * Update pagination with sliding window of visible dots
   * @param {number} currentIndex - The current slide index
   * @param {number} totalSlides - The total number of slides
   */
  updateSlidingPagination(currentIndex, totalSlides) {
    if (!this.pagination) return;

    // Maximum number of visible pagination dots
    const maxVisibleDots = 7;

    // If we have 7 or fewer dots, show them all
    if (totalSlides <= maxVisibleDots) {
      const allDots = this.pagination.querySelectorAll("li");
      allDots.forEach((dot) => {
        dot.classList.add("pagination-visible");
      });
      return;
    }

    // Get all pagination items
    const paginationItems = this.pagination.querySelectorAll("li");

    // Reset all pagination classes
    paginationItems.forEach((item) => {
      item.classList.remove("pagination-visible");
      item.classList.remove("pagination-first-visible");
      item.classList.remove("pagination-last-visible");
      item.classList.remove("pagination-scaled");
    });

    // Determine the start and end indices for the sliding window
    let startIdx = 0;

    // Forward motion (left to right)
    if (currentIndex >= 3 && currentIndex < totalSlides - 3) {
      // Center the active dot with 3 on each side when possible
      startIdx = currentIndex - 3;
    } else if (currentIndex >= totalSlides - 3) {
      // When approaching the end, keep the window at the end
      startIdx = totalSlides - maxVisibleDots;
    }

    // Apply visible class to the dots in our window
    for (let i = startIdx; i < startIdx + maxVisibleDots && i < totalSlides; i++) {
      if (paginationItems[i]) {
        paginationItems[i].classList.add("pagination-visible");

        // Mark first visible item and apply scaling
        if (i === startIdx) {
          paginationItems[i].classList.add("pagination-first-visible");

          // If we're past the 4th slide, make the first visible dot scaled down
          if (currentIndex >= 4) {
            paginationItems[i].classList.add("pagination-scaled");
          }
        }

        // Mark last visible item and apply scaling
        if (i === startIdx + maxVisibleDots - 1 || i === totalSlides - 1) {
          paginationItems[i].classList.add("pagination-last-visible");

          // Only apply scaling if we're not in the last 4 slides
          if (currentIndex < totalSlides - 4) {
            paginationItems[i].classList.add("pagination-scaled");
          }
        }
      }
    }

    // Force browser to recognize the changes
    this.pagination.style.display = "none";
    this.pagination.offsetHeight; // Force reflow
    this.pagination.style.display = "";
  }

  /**
   * Set up all event listeners
   */
  setupEventListeners() {
    window.addEventListener("resize", this.handleResize.bind(this));
    window.addEventListener("render:section:product", this.handleProductRender.bind(this));
    this.setupOptionChangeListeners();
    this.setupMediaClickListeners();
  }

  /**
   * Handle initial variant selection
   */
  handleInitialVariant() {
    const node = document.querySelector("[data-template-product]");
    if (!node) return;

    try {
      const dataElement = node.querySelector("[data-data]");
      if (!dataElement) return;

      const productData = JSON.parse(dataElement.textContent);
      if (!productData || !productData.variants) return;

      const selectedOptions = getOptions(node, productData);
      const matchingVariant = getVariant(node, productData, selectedOptions);

      if (matchingVariant && matchingVariant.id) {
        this.updateVariantImage(matchingVariant.id);
      }
    } catch (error) {
      console.error("Error handling initial variant:", error);
    }
  }

  /**
   * Called when the element is removed from the DOM
   */
  disconnectedCallback() {
    this.removeEventListeners();
    this.destroyCarousels();
  }

  /**
   * Remove all event listeners
   */
  removeEventListeners() {
    window.removeEventListener("resize", this.handleResize.bind(this));
    window.removeEventListener("render:section:product", this.handleProductRender.bind(this));
    this.cleanupOptionChangeListeners();
  }

  /**
   * Destroy all carousels
   */
  destroyCarousels() {
    if (this.mainSplide) {
      this.mainSplide.destroy();
      this.mainSplide = null;
    }

    this.destroyThumbs();
  }

  /**
   * Handle browser resize events
   */
  handleResize() {
    const isDesktop = window.innerWidth >= 990;

    if (isDesktop && !this.thumbsInitialized) {
      this.initializeThumbs();
    } else if (!isDesktop && this.thumbsInitialized) {
      this.destroyThumbs();
    }
  }

  /**
   * Initialize the thumbnail carousel
   */
  initializeThumbs() {
    if (!this.thumbsInitialized && this.thumbnailSplide) {
      // Initialize and mount thumbnail carousel
      this.thumbs = new Splide(this.thumbnailSplide, this.thumbnailOptions);
      this.thumbs.mount();

      // Connect main and thumbnail carousels
      if (this.mainSplide) {
        this.syncMainAndThumbnailCarousels();
      }

      this.thumbsInitialized = true;
    }
  }

  /**
   * Sync main and thumbnail carousels for navigation
   */
  syncMainAndThumbnailCarousels() {
    if (!this.mainSplide || !this.thumbs) return;

    this.mainSplide.options = {
      ...this.mainSplide.options,
      pagination: true,
    };

    // Get total number of slides to determine focus behavior
    const totalSlides = this.thumbs.Components.Slides.get().length;
    const perPage = this.thumbnailOptions.perPage || 7;

    // Only use center focus if we have more slides than can fit in view
    const focusSetting = totalSlides > perPage ? "center" : 0;

    this.thumbs.options = {
      ...this.thumbs.options,
      isNavigation: true,
      focus: focusSetting,
    };

    // Update thumbnail position and active state when main carousel moves
    this.mainSplide.on("move", (newIndex) => {
      // Skip thumbnail updates if thumbs doesn't exist (mobile)
      if (!this.thumbs || !this.thumbsInitialized) return;

      // Go to the slide and force the move (not just focusing)
      this.thumbs.go(newIndex);

      // Ensure thumbnails activate properly
      const slides = this.thumbs.Components?.Slides?.get();
      if (!slides) return;

      slides.forEach((slide, i) => {
        if (i === newIndex) {
          slide.slide.classList.add("is-active");
        } else {
          slide.slide.classList.remove("is-active");
        }
      });
    });

    // Better handling for thumbnail clicks
    this.thumbs.on("click", (slide) => {
      if (!this.mainSplide) return;

      // Get the index of the clicked slide
      const clickedIndex = slide.index;

      // Update main slide
      this.mainSplide.go(clickedIndex);

      // Ensure all thumbnails have correct active state
      const slides = this.thumbs.Components?.Slides?.get();
      if (!slides) return;

      slides.forEach((slide, i) => {
        slide.slide.classList.toggle("is-active", i === clickedIndex);
      });
    });

    // Add direct click handler to ensure consistent behavior
    if (!this.thumbnailSplide) return;

    const thumbnailSlides = this.thumbnailSplide.querySelectorAll(".splide__slide");
    thumbnailSlides.forEach((slide, index) => {
      slide.addEventListener("click", () => {
        if (!this.mainSplide) return;

        // Navigate main carousel
        this.mainSplide.go(index);

        // Explicitly update active states on thumbnails
        thumbnailSlides.forEach((s, i) => {
          s.classList.toggle("is-active", i === index);
        });
      });
    });
  }

  /**
   * Destroy the thumbnail carousel
   */
  destroyThumbs() {
    if (this.thumbs && this.thumbsInitialized) {
      this.thumbs.destroy();
      this.thumbs = null;
      this.thumbsInitialized = false;
    }
  }

  /**
   * Set up event listeners for option changes
   */
  setupOptionChangeListeners() {
    if (!this.productNode) return;

    this.optionInputs = this.productNode.querySelectorAll("[data-option]");
    this.boundOptionChangeHandler = this.handleOptionChange.bind(this);

    this.optionInputs.forEach((input) => {
      input.addEventListener("change", this.boundOptionChangeHandler);
    });
  }

  /**
   * Clean up option change event listeners
   */
  cleanupOptionChangeListeners() {
    if (this.optionInputs && this.boundOptionChangeHandler) {
      this.optionInputs.forEach((input) => {
        input.removeEventListener("change", this.boundOptionChangeHandler);
      });
    }
  }

  /**
   * Handle product option change events
   */
  handleOptionChange() {
    if (!this.productNode) return;

    try {
      const dataElement = this.productNode.querySelector("[data-data]");
      if (!dataElement) return;

      const productData = JSON.parse(dataElement.textContent);
      if (!productData || !productData.variants) return;

      const currentOptions = getOptions(this.productNode, productData);
      const currentVariant = getVariant(this.productNode, productData, currentOptions);

      if (currentVariant && currentVariant.id) {
        this.updateVariantImage(currentVariant.id);
      }
    } catch (error) {
      console.error("Error handling option change:", error);
    }
  }

  /**
   * Handle product section render events
   */
  handleProductRender() {
    try {
      const updatedProductNode = document.querySelector("[data-template-product]");
      if (!updatedProductNode) return;

      const dataElement = updatedProductNode.querySelector("[data-data]");
      if (!dataElement) return;

      const productData = JSON.parse(dataElement.textContent);
      if (!productData || !productData.variants) return;

      const updatedOptions = getOptions(updatedProductNode, productData);
      const updatedVariant = getVariant(updatedProductNode, productData, updatedOptions);

      if (updatedVariant && updatedVariant.id) {
        this.updateVariantImage(updatedVariant.id);
      }
    } catch (error) {
      console.error("Error handling product render:", error);
    }
  }

  /**
   * Update the first image to show the selected variant
   * @param {number|string} variantId - The selected variant ID
   */
  updateVariantImage(variantId) {
    if (!variantId) return;
    const variantImageData = this.findVariantImageData(variantId);
    if (!variantImageData) {
      // No matching variant image data found - this is normal for products without variant images
      return;
    }

    this.updateMainSlideImage(variantImageData);
    this.updateThumbnailSlideImage(variantImageData);
    this.refreshCarousels();
  }

  /**
   * Find image data for the specified variant
   * @param {number|string} variantId - The variant ID to find
   * @returns {Object|null} The variant image data or null if not found
   */
  findVariantImageData(variantId) {
    if (!this.variantImagesData || !this.variantImagesData.length) {
      return null;
    }

    const variantImageData = this.variantImagesData.find((data) => Number(data.variant_id) === Number(variantId));
    return variantImageData || null;
  }

  /**
   * Update the main carousel's first slide image
   * @param {Object} variantImageData - The variant image data
   * @returns {boolean} Whether the update was successful
   */
  updateMainSlideImage(variantImageData) {
    if (!variantImageData || !variantImageData.image_src) {
      console.info("No valid image source in variant image data");
      return false;
    }

    if (!this.mainSplide) {
      console.info("Main carousel not initialized");
      return false;
    }

    const mainFirstSlide = this.mainSplide?.Components?.Slides?.getAt(0);
    if (!mainFirstSlide) {
      console.info("Main slide not found");
      return false;
    }

    const mainImg = mainFirstSlide.slide.querySelector("img.grafikr-product__media__main__image");
    if (!mainImg) {
      console.info("No image element found in main slide");
      return false;
    }

    try {
      const imageSrc = variantImageData.image_src;
      const width = 1024; // Default width for main image
      const newMainSrc = this.createShopifyImageUrl(imageSrc, width);

      mainImg.removeAttribute("srcset");
      mainImg.src = newMainSrc;
      return true;
    } catch (error) {
      console.error("Error updating main slide image:", error);
      return false;
    }
  }

  /**
   * Update the thumbnail carousel's first slide image
   * @param {Object} variantImageData - The variant image data
   * @returns {boolean} Whether the update was successful
   */
  updateThumbnailSlideImage(variantImageData) {
    if (!variantImageData || !variantImageData.image_src) {
      console.info("No valid image source in variant image data for thumbnail update");
      return false;
    }

    // Skip if thumbnails aren't initialized (likely mobile view)
    if (!this.thumbs || !this.thumbsInitialized) {
      console.info("Thumbnail carousel not initialized");
      return false;
    }

    const thumbFirstSlide = this.thumbs?.Components?.Slides?.getAt(0);
    if (!thumbFirstSlide) {
      console.info("Thumbnail slide not found or not initialized");
      return false;
    }

    const thumbImg = thumbFirstSlide.slide.querySelector("img");
    if (!thumbImg) {
      console.info("No image element found in thumbnail slide");
      return false;
    }

    try {
      const imageSrc = variantImageData.image_src;
      const width = 80; // Default width for thumbnail
      const newThumbSrc = this.createShopifyImageUrl(imageSrc, width);

      thumbImg.src = newThumbSrc;
      return true;
    } catch (error) {
      console.error("Error updating thumbnail slide image:", error);
      return false;
    }
  }

  /**
   * Refresh both carousels to update the UI
   */
  refreshCarousels() {
    try {
      if (this.mainSplide) {
        this.mainSplide.refresh();
      }

      if (this.thumbs && this.thumbsInitialized) {
        this.thumbs.refresh();
      }
    } catch (error) {
      console.error("Error refreshing carousels:", error);
    }
  }

  /**
   * Create a properly formatted Shopify CDN image URL
   * @param {string} imageSrc - The original image source URL
   * @param {number} width - The desired image width
   * @returns {string} The formatted image URL
   */
  createShopifyImageUrl(imageSrc, width) {
    try {
      if (!imageSrc) return "";

      // Handle protocol-relative URLs that start with //
      if (imageSrc.startsWith("//")) {
        imageSrc = "https:" + imageSrc;
      } else if (imageSrc.startsWith("\\/\\/")) {
        // Handle JSON-escaped protocol-relative URLs
        imageSrc = "https:" + imageSrc.replace(/\\\//g, "/");
      }

      // Add https if the URL doesn't have a protocol
      if (!imageSrc.startsWith("http") && !imageSrc.startsWith("https:") && !imageSrc.startsWith("//")) {
        imageSrc = "https://" + imageSrc.replace(/^\/+/, "");
      }

      // Split URL to separate the query params
      const [baseUrl, queryString] = imageSrc.split("?");

      // Check if this is a Shopify CDN URL before applying transformations
      if (baseUrl.includes("cdn.shopify.com") || baseUrl.includes(".myshopify.com")) {
        // Format for Shopify's CDN image resizing - match image extensions case-insensitively
        const imageUrlWithSize = baseUrl.replace(/\.(jpg|jpeg|png|gif|webp)$/i, `_${width}x.$1`);

        // Add back the query parameters if they existed
        return queryString ? `${imageUrlWithSize}?${queryString}` : imageUrlWithSize;
      }

      // If not a Shopify CDN URL, return the original URL
      return imageSrc;
    } catch (error) {
      console.error("Error formatting Shopify image URL:", error);
      // Return the original URL if there's an error
      return imageSrc || "";
    }
  }

  /**
   * Helpers for HTML5 video handling in the main gallery
   */
  getMainVideoComponent() {
    return this.mainSplide?.Components?.Video || null;
  }

  getSlideIndexByElement(splideInstance, slideElement) {
    const slides = splideInstance?.Components?.Slides?.get?.();
    if (!slides || !slides.length) return -1;
    for (let i = 0; i < slides.length; i++) {
      if (slides[i].slide === slideElement) return i;
    }
    return -1;
  }

  isVideoSlide(slideElement) {
    if (!slideElement) return false;
    const d = slideElement.dataset || {};
    return !!(d.splideHtmlVideo || d.splideYoutube || d.splideVimeo);
  }

  setMainVideoOverlayVisible(slideElement, visible) {
    const overlay = slideElement?.querySelector?.(".grafikr-product__media__video-play");
    if (!overlay) return;
    overlay.style.display = visible ? "flex" : "none";
  }

  wireHtmlVideoAttributesForSlide(slideElement) {
    if (!slideElement) return;
    const video = slideElement.querySelector?.("video");
    if (!video) return;

    try {
      // Disable native controls but allow programmatic control
      video.controls = false;
      video.setAttribute("playsinline", "");
      video.setAttribute("webkit-playsinline", "");
      video.playsInline = true;
      video.disablePictureInPicture = true;
      video.setAttribute("controlsList", "nodownload noplaybackrate noremoteplayback");
      // Keep audio on unless merchant muted the source explicitly
      // video.muted = false;
      // Ensure visibility sizing is sane even if plugin injects the element without styles
      video.style.maxWidth = "100%";
      video.style.width = "100%";
      video.style.height = "100%";
      video.style.objectFit = "contain";
      video.style.pointerEvents = "auto";
      // Wire click-to-toggle once for the MAIN gallery
      if (!video.dataset.grafikrClickWired) {
        video.addEventListener("click", (e) => {
          e.preventDefault();
          e.stopPropagation();
          const Video = this.getMainVideoComponent();
          if (!Video) return;
          const index = this.getSlideIndexByElement(this.mainSplide, slideElement);
          if (index < 0) return;
          if (video.paused) {
            this.setMainVideoOverlayVisible(slideElement, false);
            try { Video.play(index); } catch (err) {}
          } else {
            try { Video.pause(); } catch (err) {}
            this.setMainVideoOverlayVisible(slideElement, true);
          }
        });
        video.dataset.grafikrClickWired = "true";
      }
    } catch (e) {
      console.info("Unable to wire HTML5 video attributes:", e);
    }
  }

  playHtmlVideoForSlide(slideElement) {
    if (!this.mainSplide || !slideElement) return;
    const Video = this.getMainVideoComponent();
    if (!Video) return;
    const index = this.getSlideIndexByElement(this.mainSplide, slideElement);
    if (index < 0) return;
    // Ensure attributes are correct before playing
    this.wireHtmlVideoAttributesForSlide(slideElement);
    this.setMainVideoOverlayVisible(slideElement, false);
    Video.play(index);
  }

  pauseAllMainVideos() {
    const Video = this.getMainVideoComponent();
    if (Video && typeof Video.pause === "function") {
      Video.pause();
    }
    // Restore overlays on all video slides
    try {
      const slides = this.mediaSplide?.querySelectorAll?.(".splide__slide[data-splide-html-video]");
      if (slides && slides.length) {
        slides.forEach((slide) => this.setMainVideoOverlayVisible(slide, true));
      }
    } catch (_) {}
  }

  /**
   * Set up click listeners for media slides to open lightbox
   */
  setupMediaClickListeners() {
    if (!this.mediaSplide) return;

    // Find all slides and add click listeners
    const slides = this.mediaSplide.querySelectorAll(".splide__slide");
    slides.forEach((slide) => {
      slide.addEventListener("click", (event) => {
        const target = event.target;
        const isVideoSlide = this.isVideoSlide(slide);

        // HTML5 video slides: play/pause in place, do NOT open lightbox
        if (isVideoSlide) {
          event.stopPropagation();
          event.preventDefault();
          const Video = this.getMainVideoComponent();
          if (!Video) return;
          const index = this.getSlideIndexByElement(this.mainSplide, slide);
          if (index < 0) return;
          // If the current slide is already playing, pause; otherwise play.
          const shouldPause = this.mainVideoPlayingIndex === index;
          if (shouldPause) {
            try { Video.pause(); } catch (err) {}
            this.setMainVideoOverlayVisible(slide, true);
          } else {
            this.playHtmlVideoForSlide(slide);
          }
          return;
        }

        // Non-video slides: open lightbox on image click
        if (target.tagName === "IMG" && !target.closest("button")) {
          const lightbox = document.querySelector("product-media-lightbox");
          if (lightbox) {
            lightbox.open();
          }
        }
      });
    });
  }
}

// Register the custom element
if (!customElements.get("product-media")) {
  customElements.define("product-media", ProductMedia);
}

/**
 * Product Media Lightbox Component
 *
 * Creates a fullscreen lightbox for displaying product media
 * Synchronizes with the ProductMedia component to show the correct active slide
 */
class ProductMediaLightbox extends HTMLElement {
  constructor() {
    super();
    this.initializeProperties();
    this.createLightboxStructure();
  }

  /**
   * Initialize component properties
   */
  initializeProperties() {
    // Component state
    this.isOpen = false;
    this.currentIndex = 0;
    this.productMedia = document.querySelector("product-media");
    this.mediaItems = [];
    this.lightboxSplide = null;
    this.lightboxVideoPlayingIndex = null;

    // Bind methods to maintain correct 'this' context
    this.open = this.open.bind(this);
    this.close = this.close.bind(this);
    this.handleKeydown = this.handleKeydown.bind(this);
    this.handleResize = this.handleResize.bind(this);
  }

  /**
   * Helpers for HTML5 video handling in the lightbox
   */
  getLightboxVideoComponent() {
    return this.lightboxSplide?.Components?.Video || null;
  }

  setLightboxOverlayVisible(slideElement, visible) {
    const overlay = slideElement?.querySelector?.(".grafikr-product__media__video-play");
    if (!overlay) return;
    overlay.style.display = visible ? "flex" : "none";
  }

  wireLightboxHtmlVideoAttributesForSlide(slideElement) {
    if (!slideElement) return;
    const video = slideElement.querySelector?.("video");
    if (!video) return;
    try {
      video.controls = false;
      video.setAttribute("playsinline", "");
      video.setAttribute("webkit-playsinline", "");
      video.playsInline = true;
      video.disablePictureInPicture = true;
      video.setAttribute("controlsList", "nodownload noplaybackrate noremoteplayback");
      video.style.maxWidth = "100%";
      video.style.width = "100%";
      video.style.height = "auto";
      video.style.objectFit = "contain";
      video.style.pointerEvents = "auto";
      if (!video.dataset.grafikrLbClickWired) {
        const toggle = (e) => {
          e.preventDefault();
          if (typeof e.stopImmediatePropagation === "function") e.stopImmediatePropagation();
          e.stopPropagation();
          const Video = this.getLightboxVideoComponent();
          if (!Video) return;
          try {
            if (video.paused) {
              Video.play(this.lightboxSplide.index);
              try { video.play(); } catch (_) {}
              this.setLightboxOverlayVisible(slideElement, false);
            } else {
              Video.pause();
              try { video.pause(); } catch (_) {}
              this.setLightboxOverlayVisible(slideElement, true);
            }
          } catch (_) {}
        };
        video.addEventListener("click", toggle);
        video.addEventListener("touchend", toggle, { passive: false });
        video.dataset.grafikrLbClickWired = "true";
      }
    } catch (e) {
      console.info("Unable to wire HTML5 video attributes (lightbox):", e);
    }
  }

  /**
   * Create the lightbox HTML structure
   */
  createLightboxStructure() {
    // Create main container
    this.container = document.createElement("div");
    this.container.className = "grafikr-product-media-lightbox";
    this.container.setAttribute("aria-hidden", "true");
    

    // Create overlay
    this.overlay = document.createElement("div");
    this.overlay.className = "grafikr-product-media-lightbox__overlay";
    this.overlay.addEventListener("click", (e) => {
      e.stopPropagation();
      if (typeof e.stopImmediatePropagation === "function") e.stopImmediatePropagation();
      this.close();
    });

    // Create content container
    this.content = document.createElement("div");
    this.content.className = "grafikr-product-media-lightbox__content";

    // Create close button
    this.closeButton = document.createElement("button");
    this.closeButton.className = "grafikr-product-media-lightbox__close";
    this.closeButton.setAttribute("aria-label", "Close lightbox");
    this.closeButton.innerHTML = `
      <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
        <line x1="18" y1="6" x2="6" y2="18"></line>
        <line x1="6" y1="6" x2="18" y2="18"></line>
      </svg>
    `;
    this.closeButton.addEventListener("click", (e) => {
      e.preventDefault();
      e.stopPropagation();
      if (typeof e.stopImmediatePropagation === "function") e.stopImmediatePropagation();
      this.close();
    });

    // Create navigation buttons (desktop only)
    this.prevButton = document.createElement("button");
    this.prevButton.className = "grafikr-product-media-lightbox__nav grafikr-product-media-lightbox__nav--prev";
    this.prevButton.setAttribute("aria-label", "Previous slide");
    this.prevButton.innerHTML = `
      <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
        <polyline points="15 18 9 12 15 6"></polyline>
      </svg>
    `;

    this.nextButton = document.createElement("button");
    this.nextButton.className = "grafikr-product-media-lightbox__nav grafikr-product-media-lightbox__nav--next";
    this.nextButton.setAttribute("aria-label", "Next slide");
    this.nextButton.innerHTML = `
      <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
        <polyline points="9 18 15 12 9 6"></polyline>
      </svg>
    `;

    // Create Splide container for the lightbox carousel
    this.splideContainer = document.createElement("div");
    this.splideContainer.className = "splide grafikr-product-media-lightbox__splide";
    this.splideContainer.innerHTML = `
      <div class="splide__track">
        <ul class="splide__list"></ul>
      </div>
    `;

    // Assemble the lightbox structure
    this.content.appendChild(this.closeButton);
    this.content.appendChild(this.prevButton);
    this.content.appendChild(this.nextButton);
    this.content.appendChild(this.splideContainer);

    this.container.appendChild(this.overlay);
    this.container.appendChild(this.content);

    // Append to document
    document.body.appendChild(this.container);
    
  }

  /**
   * Called when the element is added to the DOM
   */
  connectedCallback() {
    // Find the lightbox button in the product-media component
    if (this.productMedia) {
      const lightboxButton = this.productMedia.querySelector(".grafikr-product__media__lightbox-button");
      if (lightboxButton) {
        lightboxButton.addEventListener("click", this.open);
      }
    }

    // Set up event listeners
    window.addEventListener("resize", this.handleResize);
    document.addEventListener("keydown", this.handleKeydown);
  }

  /**
   * Called when the element is removed from the DOM
   */
  disconnectedCallback() {
    window.removeEventListener("resize", this.handleResize);
    document.removeEventListener("keydown", this.handleKeydown);

    // Remove lightbox button event listener
    if (this.productMedia) {
      const lightboxButton = this.productMedia.querySelector(".grafikr-product__media__lightbox-button");
      if (lightboxButton) {
        lightboxButton.removeEventListener("click", this.open);
      }
    }

    // Destroy Splide instance if it exists
    if (this.lightboxSplide) {
      this.lightboxSplide.destroy();
      this.lightboxSplide = null;
    }
  }

  /**
   * Handle window resize events
   */
  handleResize() {
    if (this.isOpen && this.lightboxSplide) {
      // Update navigation visibility based on screen size
      this.updateNavigationVisibility();

      // Update scroll direction based on screen size
      const isMobile = window.innerWidth < 990;
      const currentDirection = this.lightboxSplide.options.direction;
      const newDirection = isMobile ? "ttb" : "ltr";

      // Only update if direction has changed
      if (currentDirection !== newDirection) {
        this.lightboxSplide.options = {
          ...this.lightboxSplide.options,
          direction: newDirection,
          height: isMobile ? "100vh" : undefined,
          wheel: isMobile,
        };

        // Destroy and remount to apply new direction
        this.lightboxSplide.destroy();
        this.setupLightboxCarousel();
      } else {
        // Just refresh if direction hasn't changed
        this.lightboxSplide.refresh();
      }
    }
  }

  /**
   * Handle keyboard events for accessibility
   * @param {KeyboardEvent} event - The keyboard event
   */
  handleKeydown(event) {
    if (!this.isOpen) return;

    switch (event.key) {
      case "Escape":
        this.close();
        break;
      case "ArrowLeft":
        if (this.lightboxSplide) {
          this.lightboxSplide.go("-");
        }
        break;
      case "ArrowRight":
        if (this.lightboxSplide) {
          this.lightboxSplide.go("+");
        }
        break;
    }
  }

  /**
   * Get media items from the product-media component
   * @returns {Array} Array of media items with their type and source
   */
  getMediaItems() {
    const items = [];

    if (!this.productMedia) return items;

    // Find all slides in the main media carousel
    const slides = this.productMedia.querySelectorAll("#media-splide .splide__slide");

    slides.forEach((slide) => {
      let type = "image";
      let src = "";
      let previewImage = "";

      // Find the main product image (not badge images) by looking for the specific class
      // This avoids picking up badge images that might appear before the main image
      const mainImg = slide.querySelector("img.grafikr-product__media__main__image");
      if (mainImg) {
        previewImage = mainImg.src.replace(/_\d+x\./, "_2048x.");
      }

      // Determine the media type and source
      if (slide.dataset.splideYoutube) {
        type = "youtube";
        src = slide.dataset.splideYoutube;
      } else if (slide.dataset.splideVimeo) {
        type = "vimeo";
        src = slide.dataset.splideVimeo;
      } else if (slide.dataset.splideHtmlVideo) {
        type = "video";
        src = slide.dataset.splideHtmlVideo;
      } else {
        // Regular image
        if (previewImage) {
          src = previewImage;
        }
      }

      if (src) {
        items.push({ type, src, previewImage });
      }
    });

    return items;
  }

  /**
   * Open the lightbox
   */
  open() {
    if (this.isOpen) return;

    // Reset any leftover styles from previous close
    this.container.style.opacity = "";
    this.container.style.pointerEvents = "";

    // Get current index from product-media
    this.currentIndex = 0;
    if (this.productMedia && this.productMedia.mainSplide) {
      this.currentIndex = this.productMedia.mainSplide.index;
    }

    // Get media items
    this.mediaItems = this.getMediaItems();

    // Set up lightbox content
    this.setupLightboxCarousel();

    // Show the lightbox
    this.container.setAttribute("aria-hidden", "false");
    // Reflect on host for easier debugging/inspection
    this.setAttribute("aria-hidden", "false");
    // Ensure visible immediately
    this.container.style.display = "block";
    

    // Prevent scrolling/interactions with content underneath
    document.body.style.overflow = "hidden";
    document.body.style.touchAction = "none";

    this.isOpen = true;

    // Update navigation visibility based on screen size
    this.updateNavigationVisibility();

    // Force a refresh after a short delay to fix image sizing issues
    setTimeout(() => {
      if (this.lightboxSplide) {
        this.lightboxSplide.refresh();

        // Force the active image to recalculate dimensions
        const activeSlide = this.lightboxSplide.Components.Slides?.getAt(this.currentIndex)?.slide;
        if (activeSlide) {
          const img = activeSlide.querySelector("img");
          if (img) {
            // Force reflow/repaint
            img.style.display = "none";
            img.offsetHeight; // Trigger reflow
            img.style.display = "";
          }
        }

        // Apply explicit width to all slides for proper alignment
        const slides = this.lightboxSplide.Components.Slides?.get();
        if (slides) {
          slides.forEach((slide) => {
            slide.slide.style.width = "100%";
          });
        }
      }
    }, 50);
  }

  /**
   * Close the lightbox
   */
  close() {
    // Always attempt to hide lightbox, even if state got out of sync
    

    // Destroy Splide instance FIRST to avoid Video extension interactions
    try {
      if (this.lightboxSplide) {
        this.lightboxSplide.destroy();
        this.lightboxSplide = null;
      }
    } catch (e) {}

    // Manually pause/cleanup any remaining HTML5 videos/iframes
    try {
      const videos = this.container.querySelectorAll("video");
      videos.forEach((v) => {
        try {
          v.pause();
          // Unload the source to stop audio on some browsers
          v.removeAttribute("src");
          v.load();
        } catch (_) {}
      });
      const iframes = this.container.querySelectorAll("iframe");
      iframes.forEach((f) => {
        try {
          // Best-effort unload
          f.src = "about:blank";
        } catch (_) {}
      });
    } catch (_) {}

    // Mark as closed
    this.isOpen = false;

    // Hide lightbox immediately to avoid click-through re-open issues
    this.container.setAttribute("aria-hidden", "true");
    // Reflect on host for easier debugging/inspection
    this.setAttribute("aria-hidden", "true");
    this.container.style.opacity = "";
    this.container.style.display = "none";
    
    // Restore page scrolling
    document.body.style.overflow = "";
    document.body.style.touchAction = "";
  }

  /**
   * Setup the lightbox carousel with Splide
   */
  setupLightboxCarousel() {
    // Get the list element
    const list = this.splideContainer.querySelector(".splide__list");

    // Clear previous slides
    list.innerHTML = "";

    // Store video preview information to restore when needed
    this.videoPreviewInfo = [];

    // Create slides for each media item
    this.mediaItems.forEach((item, index) => {
      const slide = document.createElement("li");
      slide.className = "splide__slide";

      switch (item.type) {
        case "image":
          slide.innerHTML = `<div class="grafikr-product-media-lightbox__slide"><img src="${item.src}" alt="Product image" loading="eager"></div>`;
          break;
        case "youtube":
          slide.dataset.splideYoutube = item.src;
          if (item.previewImage) {
            slide.innerHTML = `
              <div class="grafikr-product-media-lightbox__slide grafikr-product-media-lightbox__slide--video">
                <img src="${item.previewImage}" alt="Video preview" class="grafikr-product-media-lightbox__video-preview" loading="eager">
                <div class="grafikr-product__media__video-play" role="button" aria-label="Play video" tabindex="0">
                  <svg xmlns="http://www.w3.org/2000/svg" aria-hidden="true" focusable="false" class="icon icon-play" fill="none" viewBox="0 0 20 20">
                    <path fill-rule="evenodd" clip-rule="evenodd" d="M8.277 12.927V7.07l4.649 2.927-4.649 2.93zm-.134-7.235a.595.595 0 00-.32-.092c-.344 0-.623.292-.623.653v7.492c0 .119.03.235.089.337.177.309.56.41.855.224l5.954-3.747a.638.638 0 00.214-.224.673.673 0 00-.214-.897L8.144 5.691z" fill="currentColor"></path>
                  </svg>
                </div>
              </div>`;
            // Store preview info for later restoration
            this.videoPreviewInfo[index] = {
              type: "youtube",
              previewImage: item.previewImage,
            };
          } else {
            slide.innerHTML = `<div class="grafikr-product-media-lightbox__slide grafikr-product-media-lightbox__slide--video"></div>`;
          }
          break;
        case "vimeo":
          slide.dataset.splideVimeo = item.src;
          if (item.previewImage) {
            slide.innerHTML = `
              <div class="grafikr-product-media-lightbox__slide grafikr-product-media-lightbox__slide--video">
                <img src="${item.previewImage}" alt="Video preview" class="grafikr-product-media-lightbox__video-preview" loading="eager">
                <div class="grafikr-product__media__video-play" role="button" aria-label="Play video" tabindex="0">
                  <svg xmlns="http://www.w3.org/2000/svg" aria-hidden="true" focusable="false" class="icon icon-play" fill="none" viewBox="0 0 20 20">
                    <path fill-rule="evenodd" clip-rule="evenodd" d="M8.277 12.927V7.07l4.649 2.927-4.649 2.93zm-.134-7.235a.595.595 0 00-.32-.092c-.344 0-.623.292-.623.653v7.492c0 .119.03.235.089.337.177.309.56.41.855.224l5.954-3.747a.638.638 0 00.214-.224.673.673 0 00-.214-.897L8.144 5.691z" fill="currentColor"></path>
                  </svg>
                </div>
              </div>`;
            // Store preview info for later restoration
            this.videoPreviewInfo[index] = {
              type: "vimeo",
              previewImage: item.previewImage,
            };
          } else {
            slide.innerHTML = `<div class="grafikr-product-media-lightbox__slide grafikr-product-media-lightbox__slide--video"></div>`;
          }
          break;
        case "video":
          slide.dataset.splideHtmlVideo = item.src;
          if (item.previewImage) {
            slide.innerHTML = `
              <div class="grafikr-product-media-lightbox__slide grafikr-product-media-lightbox__slide--video">
                <img src="${item.previewImage}" alt="Video preview" class="grafikr-product-media-lightbox__video-preview" loading="eager">
                <div class="grafikr-product__media__video-play" role="button" aria-label="Play video" tabindex="0">
                  <svg xmlns="http://www.w3.org/2000/svg" aria-hidden="true" focusable="false" class="icon icon-play" fill="none" viewBox="0 0 20 20">
                    <path fill-rule="evenodd" clip-rule="evenodd" d="M8.277 12.927V7.07l4.649 2.927-4.649 2.93zm-.134-7.235a.595.595 0 00-.32-.092c-.344 0-.623.292-.623.653v7.492c0 .119.03.235.089.337.177.309.56.41.855.224l5.954-3.747a.638.638 0 00.214-.224.673.673 0 00-.214-.897L8.144 5.691z" fill="currentColor"></path>
                  </svg>
                </div>
              </div>`;
            // Store preview info for later restoration
            this.videoPreviewInfo[index] = {
              type: "video",
              previewImage: item.previewImage,
            };
          } else {
            slide.innerHTML = `<div class="grafikr-product-media-lightbox__slide grafikr-product-media-lightbox__slide--video"></div>`;
          }
          break;
      }

      list.appendChild(slide);
    });

    // Initialize Splide with different configurations for mobile and desktop
    const isMobile = window.innerWidth < 990;

    // Ensure track and slides have proper dimensions
    const track = this.splideContainer.querySelector(".splide__track");
    if (track) {
      track.style.width = "100%";
    }

    this.lightboxSplide = new Splide(this.splideContainer, {
      type: "slide",
      pagination: false,
      arrows: false,
      start: this.currentIndex,
      focus: "center",
      flickPower: 500,
      speed: 900,
      dragMinThreshold: {
        mouse: 0,
        touch: 10,
      },
      direction: isMobile ? "ttb" : "ltr", // top-to-bottom on mobile, left-to-right on desktop
      height: isMobile ? "100vh" : undefined, // Required for vertical scrolling
      width: "100%", // Ensure full width
      wheel: isMobile, // Enable mouse wheel scrolling on mobile
      waitForTransition: true,
      video: {
        autoplay: false,
        mute: false,
        disableOverlayUI: true,
        hideControls: true,
        playerOptions: {
          htmlVideo: {
            controls: false,
            playsInline: true,
            disablePictureInPicture: true,
            disableRemotePlayback: true,
          },
        },
      },
    });

    // Pause on move to avoid background playback and restore overlays
    this.lightboxSplide.on("move", () => {
      const Video = this.getLightboxVideoComponent();
      if (Video && typeof Video.pause === "function") {
        Video.pause();
      }
      this.restoreVideoPreview();
    });

    // Set up navigation button handlers
    this.prevButton.addEventListener("click", () => {
      if (this.lightboxSplide) {
        this.lightboxSplide.go("-");
      }
    });

    this.nextButton.addEventListener("click", () => {
      if (this.lightboxSplide) {
        this.lightboxSplide.go("+");
      }
    });

    // Set up click listeners for video preview play buttons (use unified class)
    const playButtons = this.splideContainer.querySelectorAll(".grafikr-product__media__video-play");
    playButtons.forEach((button) => {
      button.addEventListener("click", () => {
        // Find the parent slide
        const slide = button.closest(".splide__slide");
        if (!slide) return;

        // Get the Splide Video component
        const Video = this.lightboxSplide.Components?.Video;
        if (!Video) return;

        // Find current index
        const slides = this.lightboxSplide.Components.Slides.get();
        const currentIndex = slides.findIndex((s) => s.slide === slide);

        if (currentIndex !== -1) {
          // Play the video at this index
          Video.play(currentIndex);

          // Hide the preview image and play button
          const preview = slide.querySelector(".grafikr-product-media-lightbox__video-preview");
          if (preview) preview.style.display = "none";
          button.style.display = "none";
        }
      });
    });

    // Handle video events
    this.lightboxSplide.on("video:play", () => {
      // When a video starts playing, hide its preview image and play button
      const activeSlide = this.lightboxSplide.Components.Slides.getAt(this.lightboxSplide.index).slide;
      const preview = activeSlide.querySelector(".grafikr-product-media-lightbox__video-preview");
      const playButton = activeSlide.querySelector(".grafikr-product__media__video-play");
      const videoEl = activeSlide.querySelector("video");
      const wrapper = activeSlide.querySelector(".splide__video__wrapper");

      if (preview) preview.style.display = "none";
      if (playButton) playButton.style.display = "none";
      if (videoEl) {
        try {
          videoEl.controls = false;
          videoEl.style.display = "";
          videoEl.style.opacity = "1";
        } catch (_) {}
      }
      if (wrapper) {
        try { wrapper.style.display = ""; } catch (_) {}
      }
      this.lightboxVideoPlayingIndex = this.lightboxSplide.index;
    });

    // Handle ended to allow replay seamlessly
    this.lightboxSplide.on("video:ended", () => {
      const activeSlide = this.lightboxSplide.Components.Slides.getAt(this.lightboxSplide.index).slide;
      const playButton = activeSlide.querySelector(".grafikr-product__media__video-play");
      const preview = activeSlide.querySelector(".grafikr-product-media-lightbox__video-preview");
      const videoEl = activeSlide.querySelector("video");
      const wrapper = activeSlide.querySelector(".splide__video__wrapper");
      if (playButton) playButton.style.display = "";
      if (preview) preview.style.display = "none";
      if (wrapper) wrapper.style.display = "";
      if (videoEl) {
        try {
          // Reset to start so replay works with Splide Video
          videoEl.pause();
          videoEl.currentTime = 0;
        } catch (_) {}
      }
      this.lightboxVideoPlayingIndex = null;
      
    });

    // Wire attributes when HTML5 video is created/loaded and toggle overlay on pause
    this.lightboxSplide.on("video:loaded", () => {
      const activeSlide = this.lightboxSplide.Components.Slides.getAt(this.lightboxSplide.index).slide;
      this.wireLightboxHtmlVideoAttributesForSlide(activeSlide);
      this.lightboxSplide.refresh();
      
    });

    this.lightboxSplide.on("video:pause", () => {
      const activeSlide = this.lightboxSplide.Components.Slides.getAt(this.lightboxSplide.index).slide;
      const playButton = activeSlide.querySelector(".grafikr-product__media__video-play");
      const preview = activeSlide.querySelector(".grafikr-product-media-lightbox__video-preview");
      const videoEl = activeSlide.querySelector("video");
      const wrapper = activeSlide.querySelector(".splide__video__wrapper");
      if (playButton) playButton.style.display = "";
      // Keep the paused frame visible rather than switching back to preview
      if (preview) preview.style.display = "none";
      if (videoEl) {
        try {
          videoEl.style.display = "";
          // Do not re-hide the video; ensure it's the visible layer
        } catch (_) {}
      }
      if (wrapper) {
        try { wrapper.style.display = ""; } catch (_) {}
      }
      
      this.lightboxVideoPlayingIndex = null;
    });

    // Add event listener for slide change to restore video preview images
    this.lightboxSplide.on("moved", () => {
      this.restoreVideoPreview();
    });

    // Add direction data attribute for CSS targeting
    this.splideContainer.setAttribute("data-splide-direction", isMobile ? "ttb" : "ltr");

    // Mount Splide with extensions
    this.lightboxSplide.mount(window.splide.Extensions);

    // Initialize video preview states
    this.restoreVideoPreview();

    // Note: click-to-toggle for the HTML5 video is wired on the <video> itself
    // in wireLightboxHtmlVideoAttributesForSlide() to avoid duplicate toggle handlers.
  }

  /**
   * Restore video preview image when returning to a slide
   */
  restoreVideoPreview() {
    if (!this.lightboxSplide || !this.videoPreviewInfo) return;

    const currentIndex = this.lightboxSplide.index;
    const slides = this.lightboxSplide.Components.Slides.get();

    // Process all slides to restore preview images
    slides.forEach((slideObj, index) => {
      const slide = slideObj.slide;
      const previewInfo = this.videoPreviewInfo[index];

      if (!previewInfo) return;

      // Check if this slide has a video container but no preview image visible
      const videoContainer = slide.querySelector(".grafikr-product-media-lightbox__slide--video");
      if (!videoContainer) return;

      // Skip the currently playing slide
      if (index === currentIndex && this.isVideoPlaying(slide)) return;

      // Check for existing preview image
      let previewImg = slide.querySelector(".grafikr-product-media-lightbox__video-preview");
      let playButton = slide.querySelector(".grafikr-product__media__video-play");

      // If preview image doesn't exist or is hidden, restore it
      if (!previewImg || previewImg.style.display === "none") {
        if (!previewImg) {
          // Create new preview image if it doesn't exist
          previewImg = document.createElement("img");
          previewImg.src = previewInfo.previewImage;
          previewImg.alt = "Video preview";
          previewImg.className = "grafikr-product-media-lightbox__video-preview";
          previewImg.loading = "eager";
          videoContainer.appendChild(previewImg);
        } else {
          // Show existing preview image
          previewImg.style.display = "";
        }
      }

      // Restore play button if it's hidden or doesn't exist
      if (!playButton) {
        playButton = document.createElement("div");
        playButton.className = "grafikr-product__media__video-play";
        playButton.setAttribute("role", "button");
        playButton.setAttribute("aria-label", "Play video");
        playButton.setAttribute("tabindex", "0");
        playButton.innerHTML = `
          <svg xmlns="http://www.w3.org/2000/svg" aria-hidden="true" focusable="false" class="icon icon-play" fill="none" viewBox="0 0 20 20">
            <path fill-rule="evenodd" clip-rule="evenodd" d="M8.277 12.927V7.07l4.649 2.927-4.649 2.93zm-.134-7.235a.595.595 0 00-.32-.092c-.344 0-.623.292-.623.653v7.492c0 .119.03.235.089.337.177.309.56.41.855.224l5.954-3.747a.638.638 0 00.214-.224.673.673 0 00-.214-.897L8.144 5.691z" fill="currentColor"></path>
          </svg>
        `;
        videoContainer.appendChild(playButton);

        // Add click event to newly created play button
        playButton.addEventListener("click", () => {
          const Video = this.lightboxSplide.Components?.Video;
          if (!Video) return;

          // Play the video
          Video.play(index);

          // Hide preview and play button
          previewImg.style.display = "none";
          playButton.style.display = "none";
        });
      } else if (playButton.style.display === "none") {
        playButton.style.display = "";
      }
    });
  }

  /**
   * Check if a video is currently playing in a slide
   * @param {HTMLElement} slide - The slide element to check
   * @returns {boolean} - Whether a video is playing
   */
  isVideoPlaying(slide) {
    // Check for playing HTML5 video
    const htmlVideo = slide.querySelector("video");
    if (htmlVideo && !htmlVideo.paused && !htmlVideo.ended) {
      return true;
    }

    // Check for iframe (YouTube/Vimeo)
    const iframe = slide.querySelector("iframe");
    if (iframe) {
      return true; // Assume iframe means video is loaded/playing
    }

    return false;
  }

  /**
   * Pause all media (videos) that might be playing
   */
  pauseAllMedia() {
    // Guarded, safe pause for when Splide Video extension is fragile
    try {
      if (this.container) {
        const videos = this.container.querySelectorAll("video");
        videos.forEach((v) => {
          try {
            v.pause();
            v.removeAttribute("src");
            v.load();
          } catch (_) {}
        });
      }
    } catch (_) {}

    try {
      if (!this.lightboxSplide) return;
      const Components = this.lightboxSplide.Components;
      const Video = Components?.Video;
      const hasHtml5 = !!this.container?.querySelector?.("video");
      const hasIframe = !!this.container?.querySelector?.("iframe");
      if (Video && typeof Video.pause === "function" && (hasHtml5 || hasIframe)) {
        Video.pause();
      }
    } catch (e) {
      console.debug("[Lightbox] pauseAllMedia: Video.pause() failed (ignored)", e);
    }
  }

  /**
   * Update navigation button visibility based on screen size
   */
  updateNavigationVisibility() {
    const isMobile = window.innerWidth < 990;

    // Show/hide navigation buttons based on screen size
    this.prevButton.style.display = isMobile ? "none" : "flex";
    this.nextButton.style.display = isMobile ? "none" : "flex";
  }
}

// Register the custom element
if (!customElements.get("product-media-lightbox")) {
  customElements.define("product-media-lightbox", ProductMediaLightbox);
}

// Get initial product data (kept outside the class for global initialization)
const node = document.querySelector("[data-template-product]");
const productData = node ? JSON.parse(node.querySelector("[data-data]").textContent) : null;
const selectedOptions = node ? getOptions(node, productData) : null;
const matchingVariant = node && productData ? getVariant(node, productData, selectedOptions) : null;
